// RetrievePage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { retrieveCypher } from "./api";
import CryptoJS from "crypto-js";
import {
  LuCheckCircle,
  LuCopy,
  LuLock,
  LuMail,
  LuShieldCheck,
  LuUnlock,
} from "react-icons/lu";
import "./App.css";
import { parseSecretPayload } from "./payload";

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    uuid: string;
    cypher: string;
    expiration: string;
  } | null;
}

function maskSecret(value: string) {
  return value.replace(/[^\r\n]/g, "*");
}

function secretMailto(secret: string, note: string) {
  const params = new URLSearchParams();
  params.set("subject", note.trim() || "PassLink secret");
  params.set("body", secret);
  return `mailto:?${params.toString().replace(/\+/g, "%20")}`;
}

type RetrieveError = "unavailable" | "decrypt" | "server";

const RETRIEVE_ERRORS: Record<
  RetrieveError,
  { title: string; description: string }
> = {
  unavailable: {
    title: "Secret unavailable",
    description:
      "This secret may have expired, been removed, or the link may be invalid.",
  },
  decrypt: {
    title: "Unable to decrypt secret",
    description:
      "The link may be incomplete or contain an invalid decryption key.",
  },
  server: {
    title: "Something went wrong",
    description: "The secret could not be retrieved. Please try again later.",
  },
};

function retrieveErrorFromMessage(message: string): RetrieveError {
  const normalized = message.toLowerCase();
  if (
    normalized.includes("not found") ||
    normalized.includes("expired") ||
    normalized.includes("invalid url")
  ) {
    return "unavailable";
  }
  return "server";
}

const RetrievePage: React.FC = () => {
  const { uuid, password } = useParams<{ uuid: string; password: string }>();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [lockJump, setLockJump] = useState(0);
  const [secret, setSecret] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<RetrieveError | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  const handleHome = () => {
    navigate("/");
  };

  const handleToggleVisibility = () => {
    setIsVisible((current) => !current);
    setLockJump((current) => current + 1);
  };

  const decryptAES = (ciphertext: string, secretKey: string): string => {
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, secretKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (err) {
      console.error("Decryption error", err);
      return "";
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(message);
    setMessageCopied(true);
    setTimeout(() => {
      setMessageCopied(false);
    }, 1500);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid || !password) {
        setError("unavailable");
        setLoading(false);
        return;
      }

      const response: ApiResponse = await retrieveCypher(uuid);
      if (response.success && response.data) {
        const decrypted = decryptAES(response.data.cypher, password);
        if (decrypted) {
          const payload = parseSecretPayload(decrypted);
          setSecret(payload.secret);
          setMessage(payload.message);
        } else {
          setError("decrypt");
        }
      } else {
        setError(retrieveErrorFromMessage(response.message || "Secret not found."));
      }
      setLoading(false);
    };

    fetchData();
  }, [uuid, password]);

  if (loading)
    return (
      <div className="App">
        <main>
          <h1
            className="back-to-future-title scale-up-reveal zoom-on-click"
            onClick={handleHome}
          >
            PassLink
          </h1>
          <p className="slide-in-left">Loading...</p>
        </main>
      </div>
    );
  if (error) {
    const copy = RETRIEVE_ERRORS[error];
    return (
      <div className="App">
        <main>
          <h1
            className="back-to-future-title scale-up-reveal zoom-on-click"
            onClick={handleHome}
          >
            PassLink
          </h1>
          <div className="retrieve-error scale-up-reveal">
            <div className="retrieve-error-mark" aria-hidden="true">
              <LuLock />
            </div>
            <h2 className="retrieve-error-title">{copy.title}</h2>
            <p className="retrieve-error-text">{copy.description}</p>
            <button
              type="button"
              className="retrieve-error-action"
              onClick={handleHome}
            >
              + Create a new secret
            </button>
          </div>
        </main>
      </div>
    );
  }

  const visibilityLabel = isVisible ? "Hide secret" : "Reveal secret";
  const companionMessage = message.trim() ? message : "";

  return (
    <div className="App">
      <main>
        <h1
          className="back-to-future-title scale-up-reveal zoom-on-click"
          onClick={handleHome}
        >
          PassLink
        </h1>
        <div className="secret-ready scale-up-reveal">
          <p className="link-ready-title secret-ready-title">
            <LuShieldCheck />
            Your secret is ready
          </p>
          {companionMessage && (
            <div className="companion-message">
              <p>Message: {companionMessage}</p>
              <button
                type="button"
                className="message-copy-btn"
                onClick={copyMessage}
                title={messageCopied ? "Copied!" : "Copy message"}
                aria-label={messageCopied ? "Copied!" : "Copy message"}
              >
                {messageCopied ? <LuCheckCircle /> : <LuCopy />}
              </button>
            </div>
          )}
          <div className={`secret ${isVisible ? "" : "masked"}`}>
            <span className="secret-value">
              {isVisible ? secret : maskSecret(secret)}
            </span>
            <button
              type="button"
              className="secret-eye-btn"
              onClick={handleToggleVisibility}
              title={visibilityLabel}
              aria-label={visibilityLabel}
            >
              <span
                key={lockJump}
                className={lockJump ? "secret-lock-icon secret-lock-jump" : "secret-lock-icon"}
              >
                {isVisible ? <LuUnlock /> : <LuLock />}
              </span>
            </button>
          </div>
        </div>
        <div className="copy-secret slide-in-right">
          <button
            type="button"
            className="encrypt-btn copy-secret-btn"
            onClick={copyToClipboard}
          >
            {copied ? <LuCheckCircle /> : <LuCopy />}
            {copied ? "Copied!" : "Copy secret"}
          </button>
          <a className="mailto-secret-btn" href={secretMailto(secret, companionMessage)}>
            <LuMail />
            Email secret
          </a>
          <p className="link-ready-subtitle">
            Click the lock to reveal your secret
          </p>
        </div>
      </main>
    </div>
  );
};

export default RetrievePage;
